import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Cookies() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'fr';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <button
          onClick={() => navigate('/')}
          className="text-primary-600 hover:text-primary-700 font-semibold mb-6"
        >
          ← {lang === 'fr' ? 'Retour' : 'Back'}
        </button>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {lang === 'fr' ? 'Politique de Cookies' : 'Cookie Policy'}
        </h1>

        {lang === 'fr' ? (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Dernière mise à jour :</strong> 31 janvier 2026
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Qu'est-ce qu'un cookie ?</h2>
              <p className="text-gray-700 mb-4">
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, smartphone, tablette) 
                lors de la visite d'un site web. Il permet au site de mémoriser des informations sur votre visite, 
                comme votre langue préférée et d'autres paramètres.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Cookies utilisés sur ProjectBoost</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.1. Cookies strictement nécessaires</h3>
              <p className="text-gray-700 mb-4">
                Ces cookies sont indispensables au fonctionnement de la Plateforme et ne peuvent être désactivés :
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 pr-4">Cookie</th>
                      <th className="text-left py-2 pr-4">Finalité</th>
                      <th className="text-left py-2">Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-4 font-mono text-xs">firebase_auth</td>
                      <td className="py-2 pr-4">Authentification de l'utilisateur</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-4 font-mono text-xs">user_session</td>
                      <td className="py-2 pr-4">Maintien de la connexion</td>
                      <td className="py-2">7 jours</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">i18n_lang</td>
                      <td className="py-2 pr-4">Préférence linguistique</td>
                      <td className="py-2">1 an</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2. Cookies de performance</h3>
              <p className="text-gray-700 mb-4">
                Ces cookies nous permettent d'améliorer le fonctionnement de la Plateforme en collectant des 
                informations anonymes sur son utilisation :
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 pr-4">Cookie</th>
                      <th className="text-left py-2 pr-4">Finalité</th>
                      <th className="text-left py-2">Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">_ga</td>
                      <td className="py-2 pr-4">Google Analytics - statistiques d'utilisation</td>
                      <td className="py-2">2 ans</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3. Cookies tiers</h3>
              <p className="text-gray-700 mb-4">
                Nous utilisons des services tiers qui peuvent déposer leurs propres cookies :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Stripe :</strong> traitement sécurisé des paiements</li>
                <li><strong>Firebase/Google Cloud :</strong> hébergement et authentification</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Gestion de vos préférences</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1. Paramétrage du navigateur</h3>
              <p className="text-gray-700 mb-4">
                Vous pouvez configurer votre navigateur pour accepter ou refuser les cookies :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Chrome :</strong> Paramètres → Confidentialité et sécurité → Cookies</li>
                <li><strong>Firefox :</strong> Options → Vie privée et sécurité → Cookies et données de sites</li>
                <li><strong>Safari :</strong> Préférences → Confidentialité → Cookies</li>
                <li><strong>Edge :</strong> Paramètres → Cookies et autorisations de site</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2. Impact du refus des cookies</h3>
              <p className="text-gray-700 mb-4">
                Le refus des cookies strictement nécessaires peut affecter votre expérience sur la Plateforme :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Impossibilité de se connecter</li>
                <li>Perte des préférences linguistiques</li>
                <li>Fonctionnalités limitées</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Cookies de réseaux sociaux</h2>
              <p className="text-gray-700 mb-4">
                Actuellement, ProjectBoost n'utilise pas de boutons de partage sur les réseaux sociaux. 
                Si cette fonctionnalité est ajoutée à l'avenir, vous en serez informé et pourrez gérer vos préférences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Durée de conservation</h2>
              <p className="text-gray-700 mb-4">
                Les cookies ont des durées de vie variables selon leur type :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Cookies de session :</strong> supprimés à la fermeture du navigateur</li>
                <li><strong>Cookies persistants :</strong> de 7 jours à 2 ans maximum</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Mises à jour de cette politique</h2>
              <p className="text-gray-700 mb-4">
                Cette politique de cookies peut être mise à jour pour refléter les changements dans nos pratiques 
                ou pour d'autres raisons opérationnelles, légales ou réglementaires.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Pour en savoir plus</h2>
              <p className="text-gray-700 mb-4">
                Pour plus d'informations sur les cookies et la protection de vos données :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>
                  <a href="https://www.cnil.fr/fr/cookies-et-autres-traceurs" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="text-primary-600 hover:underline">
                    CNIL - Cookies et traceurs
                  </a>
                </li>
                <li>
                  Consultez notre <a href="/confidentialite" className="text-primary-600 hover:underline">Politique de Confidentialité</a>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Contact</h2>
              <p className="text-gray-700 mb-4">
                Pour toute question concernant cette politique de cookies :
              </p>
              <p className="text-gray-700 mb-2">
                Email : <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline">ace.direction22@gmail.com</a>
              </p>
            </section>
          </>
        ) : (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Last updated:</strong> January 31, 2026
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. What is a cookie?</h2>
              <p className="text-gray-700 mb-4">
                A cookie is a small text file stored on your device (computer, smartphone, tablet) when visiting 
                a website. It allows the site to remember information about your visit, such as your preferred 
                language and other settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Cookies used on ProjectBoost</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.1. Strictly Necessary Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are essential for the Platform to function and cannot be disabled:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 pr-4">Cookie</th>
                      <th className="text-left py-2 pr-4">Purpose</th>
                      <th className="text-left py-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-4 font-mono text-xs">firebase_auth</td>
                      <td className="py-2 pr-4">User authentication</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 pr-4 font-mono text-xs">user_session</td>
                      <td className="py-2 pr-4">Maintain login session</td>
                      <td className="py-2">7 days</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">i18n_lang</td>
                      <td className="py-2 pr-4">Language preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2. Performance Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies help us improve the Platform's performance by collecting anonymous usage information:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 pr-4">Cookie</th>
                      <th className="text-left py-2 pr-4">Purpose</th>
                      <th className="text-left py-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 pr-4 font-mono text-xs">_ga</td>
                      <td className="py-2 pr-4">Google Analytics - usage statistics</td>
                      <td className="py-2">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.3. Third-party Cookies</h3>
              <p className="text-gray-700 mb-4">
                We use third-party services that may set their own cookies:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Stripe:</strong> secure payment processing</li>
                <li><strong>Firebase/Google Cloud:</strong> hosting and authentication</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Managing your preferences</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1. Browser settings</h3>
              <p className="text-gray-700 mb-4">
                You can configure your browser to accept or reject cookies:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2. Impact of refusing cookies</h3>
              <p className="text-gray-700 mb-4">
                Refusing strictly necessary cookies may affect your experience on the Platform:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Unable to log in</li>
                <li>Loss of language preferences</li>
                <li>Limited functionality</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Social media cookies</h2>
              <p className="text-gray-700 mb-4">
                ProjectBoost does not currently use social media sharing buttons. If this feature is added 
                in the future, you will be informed and will be able to manage your preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Retention period</h2>
              <p className="text-gray-700 mb-4">
                Cookies have varying lifespans depending on their type:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Session cookies:</strong> deleted when the browser is closed</li>
                <li><strong>Persistent cookies:</strong> from 7 days up to 2 years maximum</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Updates to this policy</h2>
              <p className="text-gray-700 mb-4">
                This cookie policy may be updated to reflect changes in our practices or for other operational, 
                legal, or regulatory reasons.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Learn more</h2>
              <p className="text-gray-700 mb-4">
                For more information on cookies and data protection:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>
                  <a href="https://www.cnil.fr/fr/cookies-et-autres-traceurs" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="text-primary-600 hover:underline">
                    CNIL - Cookies and trackers
                  </a>
                </li>
                <li>
                  Consult our <a href="/confidentialite" className="text-primary-600 hover:underline">Privacy Policy</a>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Contact</h2>
              <p className="text-gray-700 mb-4">
                For any questions regarding this cookie policy:
              </p>
              <p className="text-gray-700 mb-2">
                Email: <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline">ace.direction22@gmail.com</a>
              </p>
            </section>
          </>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>© 2026 BEL-AN Prod - ProjectBoost. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}

export default Cookies;
